import React, { FC, useState } from "react";
import { Badge } from "@mui/material";
import { useTranslations } from "next-intl";
import CalendarToday from "@/components/icons/CalendarToday/CalendarToday";
import { formatDateToFullString } from "@/helpers/formatDate";
import { useRouter } from "next/router";
import UsersIcon from "@/components/icons/UsersIcon";
import CopyIcon from "@/components/icons/CopyIcon";
import DollarIcon from "@/components/icons/DollarIcon/DollarIcon";

interface BookingDetailsCardProps {
  bookingData: any;
}

const BookingDetailsCard: FC<BookingDetailsCardProps> = ({ bookingData }) => {
  const [copied, setCopied] = useState(false);
  const router = useRouter();
  const t = useTranslations();
  const locale = router.locale;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(bookingData.trip.reference));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleNavigateToItinerary = (bookingRef: string) => {
    router.push(`/itinerary/${bookingRef}`, undefined, {
      locale: router.locale,
    });
  };

  const rooms = bookingData.hotel?.rooms || [];
  const adultsCount = rooms.reduce(
    (total: number, room: any) => total + (room.adults || 0),
    0,
  );
  const childrenCount = rooms.reduce(
    (total: number, room: any) => total + (room.children || 0),
    0,
  );

  return (
    <article className="w-full rounded-xl border border-[#d8dcde] p-4 space-y-4 min-w-[500px]">
      <Badge
        className={`w-fit flex items-center h-6 text-[#333] text-xs rounded font-semibold px-2 ${
          bookingData.trip.status === "CANCELLED"
            ? "bg-[#fcb4b4]"
            : bookingData.trip.status === "FINALIZED"
              ? "bg-[#D2FFEB]"
              : "bg-[#FFC163]"
        }`}
      >
        {bookingData.trip.status === "FINALIZED"
          ? t("isVouchered")
          : bookingData.trip.status}
      </Badge>
      <header className="flex items-center justify-between  pl-0 pt-0 pb-4  relative self-stretch w-full border-b-[0.8px] [border-bottom-style:solid] border-[#d8dcde]">
        <div className="inline-flex items-center gap-4 relative ">
          <div className="relative w-14 h-14 rounded-xl">
            <img
              src={bookingData.hotel.image}
              alt=""
              className="inset-0 w-full h-full object-cover rounded-xl bg-[#00000033]"
            />
          </div>
          <h2 className="font-semibold text-[#333] text-base mb-1">
            {bookingData.hotel.name}
          </h2>
        </div>
        <button
          onClick={() => handleNavigateToItinerary(bookingData.trip.reference)}
          className="max-c-md:w-full max-c-md:justify-center !min-w-fit flex px-4 gap-2 items-center bg-primary rounded-[100px] h-10 text-white"
        >
          <span>{t("details")}</span>
        </button>
      </header>
      <div className="flex items-start gap-4 self-stretch w-full relative ">
        <div className="min-w-5 max-w-5">
          <CalendarToday />
        </div>
        <p className="font-normal text-[#333333] text-sm ">
          {formatDateToFullString(bookingData.trip.startDate, locale)} -{" "}
          {formatDateToFullString(bookingData.trip.endDate, locale)}
        </p>
      </div>
      <div className=" ps-1 flex items-center gap-4 self-stretch w-full relative ">
        <div className="min-w-5 max-w-5">
          <UsersIcon />
        </div>
        <div className="inline-flex flex-col items-start relative -ms-[2px]">
          <p className="relative flex items-center justify-center w-fit gap-1  font-normal text-[#333333] text-sm tracking-[0] leading-5 ">
            <span>
              {bookingData.hotel.rooms.length} {t("room_label")} x{" "}
            </span>
            <span>
              {" "}
              {adultsCount} {t("adults_label")}
            </span>

            {childrenCount > 0 && (
              <span>
                {" "}
                , {childrenCount} {t("children_label")}
              </span>
            )}
          </p>
        </div>
      </div>

      <hr className="border-[#d8dcde]" />

      <section className="space-y-1">
        <label className="text-xs text-[#66797f]">
          {t("booking_reference_label")}
        </label>

        <div className="flex items-center  gap-4">
          <span className="text-lg font-semibold text-[#333]">
            {bookingData.trip.reference}
          </span>

          <button
            onClick={handleCopy}
            className="flex items-center gap-2 text-primary hover:opacity-80 focus:ring-2 focus:ring-primary rounded"
          >
            <CopyIcon />
            <span className="text-sm font-semibold">
              {copied ? t("copied") : t("copy")}
            </span>
          </button>
        </div>

        <p className="text-xs text-[#66797f]">
          {t("booking_reference_description")}
        </p>
      </section>
    </article>
  );
};

export default BookingDetailsCard;
